import React from 'react';
import { DataTable, type DataTableStateEvent } from 'primereact/datatable';

import { Column } from 'primereact/column';
import { type ITelematics } from '../../../models/User';
import DateUtils from '../../../utils/DateTimeUtils';
import { formatMileage, formatNumber, noop } from '../../../utils/GeneralUtils';
import useTenantStore, { selectTenantId } from '../../../stores/TenantStore';
import OpenAPI from '../../../endpoint/Openapi';
import { type paths } from '../../../endpoint/Schema';

const ROWS = 20;

interface ITelematicsProps {
  userId: string;
}

function Telematics({ userId }: ITelematicsProps) {
  const tenantId = useTenantStore(selectTenantId);
  const [lazyParams, setLazyParams] = React.useState<
    Pick<DataTableStateEvent, 'first' | 'rows' | 'page'>
  >({
    first: 0,
    rows: ROWS,
    page: 1,
  });
  const [loading, setLoading] = React.useState(false);
  const [data, setData] =
    React.useState<
      paths['/api/v2/admin/users/{userId}/telematics']['get']['responses']['200']['content']['application/json']
    >();

  React.useEffect(() => {
    const getTrips = async () => {
      setLoading(true);

      try {
        const res = await OpenAPI.GET(
          '/api/v2/admin/users/{userId}/telematics',
          {
            params: {
              path: { userId },
              query: {
                tenantId,
                skip: lazyParams.first,
                take: lazyParams.rows,
              },
            },
          },
        );

        setData(res.data);
      } catch {}

      setLoading(false);
    };

    getTrips().catch(noop);
  }, [lazyParams.first, lazyParams.rows, tenantId, userId]);

  function onPage(event: DataTableStateEvent) {
    setLazyParams((prev) => ({ ...prev, ...event }));
  }

  function fromDateBodyTemplate(rowData: ITelematics) {
    return DateUtils.formatDate(rowData.fromDate);
  }

  function toDateBodyTemplate(rowData: ITelematics) {
    return DateUtils.formatDate(rowData.toDate);
  }

  function safetyScoreBodyTemplate(rowData: ITelematics) {
    return formatNumber(rowData.safetyScore, 0);
  }

  function greenScoreBodyTemplate(rowData: ITelematics) {
    return formatNumber(rowData.greenScore, 0);
  }

  function speedingScoreBodyTemplate(rowData: ITelematics) {
    return formatNumber(rowData.speedingScore, 0);
  }

  function accelerationScoreBodyTemplate(rowData: ITelematics) {
    return formatNumber(rowData.accelerationScore, 0);
  }

  function phoneUsageScoreBodyTemplate(rowData: ITelematics) {
    return formatNumber(rowData.phoneUsageScore, 0);
  }

  function corneringScoreBodyTemplate(rowData: ITelematics) {
    return formatNumber(rowData.corneringScore, 0);
  }

  function brakingScoreBodyTemplate(rowData: ITelematics) {
    return formatNumber(rowData.brakingScore, 0);
  }

  function milesBodyTemplate(rowData: ITelematics) {
    return formatMileage(rowData.mileage);
  }

  function nightDrivingTimeBodyTemplate(rowData: ITelematics) {
    return formatNumber(rowData.nightDrivingTime, 0);
  }

  function milesLastWeekBodyTemplate(rowData: ITelematics) {
    return formatMileage(rowData.lastWeekMileage);
  }

  function lastWeekTripsBodyTemplate(rowData: ITelematics) {
    return formatNumber(rowData.lastWeekTrips, 0);
  }

  return (
    <div style={{ maxWidth: window.innerWidth * 0.8 }}>
      <DataTable
        value={data?.page ?? []}
        paginator
        rows={ROWS}
        totalRecords={data?.count ?? 0}
        rowHover
        lazy
        first={lazyParams.first}
        onPage={onPage}
        loading={loading}
        size="small"
      >
        <Column
          field="fromDate"
          header="From"
          body={fromDateBodyTemplate}
          dataType="date"
        />
        <Column
          field="toDate"
          header="To"
          body={toDateBodyTemplate}
          dataType="date"
        />
        <Column
          field="safetyScore"
          header="Driving score"
          body={safetyScoreBodyTemplate}
        />
        <Column
          field="greenScore"
          header="Green score"
          body={greenScoreBodyTemplate}
        />
        <Column
          field="speedingScore"
          header="Speeding score"
          body={speedingScoreBodyTemplate}
        />
        <Column
          field="accelerationScore"
          header="Acceleration score"
          body={accelerationScoreBodyTemplate}
        />
        <Column
          field="phoneUsageScore"
          header="Phone usage score"
          body={phoneUsageScoreBodyTemplate}
        />
        <Column
          field="corneringScore"
          header="Cornering score"
          body={corneringScoreBodyTemplate}
        />
        <Column
          field="brakingScore"
          header="Braking score"
          body={brakingScoreBodyTemplate}
        />
        <Column field="mileage" header="Mileage" body={milesBodyTemplate} />
        <Column
          field="nightDrivingTime"
          header="Night Driving Time"
          body={nightDrivingTimeBodyTemplate}
        />
        <Column
          field="lastWeekMileage"
          header="Mileage last week"
          body={milesLastWeekBodyTemplate}
        />
        <Column
          field="lastWeekTrips"
          header="Last week trips"
          body={lastWeekTripsBodyTemplate}
        />
      </DataTable>
    </div>
  );
}

export default Telematics;
